import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { PageContainer } from '@/components/page-container';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useAdminApiQuery, useAdminApiMutation } from '@/hooks/use-admin-api-query';
import { qk } from '@/lib/query/keys';

const PRINCIPAL_STATUSES = ['active', 'draining', 'restricted', 'retired', 'verify_only'] as const;
const FLOW_STATUSES = ['active', 'draining', 'restricted', 'retired'] as const;

export interface MetaAppFlowRow {
  id: string;
  loginFlow: string;
  configId: string | null;
  status: string;
  weight: number;
}

export interface MetaAppPrincipalRow {
  id: string;
  key: string;
  kind: string;
  appId: string;
  metaDevAppId: string | null;
  status: string;
  restrictionEpoch: number;
  connectionCount: number;
  flows: MetaAppFlowRow[];
}

interface RegistryResponse {
  principals: MetaAppPrincipalRow[];
}

/** One row in the Apps table: the principal's key, kind, live connection
 * count, and a status control (the "flip the switch to another app" lever
 * this whole project exists for). */
function PrincipalRow({ principal, onSaved }: { principal: MetaAppPrincipalRow; onSaved: () => void }) {
  const patchPrincipal = useAdminApiMutation<unknown, { status: string }>(
    (vars) => ({
      path: `/admin/meta-apps/principals/${principal.id}`,
      init: { method: 'PATCH', body: JSON.stringify(vars) },
      fallback: `Failed to update ${principal.key}`,
    }),
    { onSuccess: onSaved },
  );

  function handleStatusChange(status: string) {
    // Restricting an app stops it taking new signups and starts the
    // reconnect-email outbox for every workspace already bound to it --
    // a real production action, not a cosmetic toggle. Confirm it.
    if (
      status === 'restricted' &&
      !window.confirm(`Restrict ${principal.key}? Existing channels keep working; no new signups will use it.`)
    ) {
      return;
    }
    patchPrincipal.mutate(
      { status },
      {
        onSuccess: () => toast.success(`${principal.key} is now ${status}`),
        onError: () => toast.error(`Failed to update ${principal.key}`),
      },
    );
  }

  return (
    <TableRow>
      <TableCell className="font-medium">{principal.key}</TableCell>
      <TableCell>
        <Badge variant="secondary">{principal.kind}</Badge>
      </TableCell>
      <TableCell>
        <span role="status" aria-label={`${principal.key} connection count`}>
          {principal.connectionCount}
        </span>
      </TableCell>
      <TableCell>
        <select
          aria-label={`${principal.key} status`}
          defaultValue={principal.status}
          disabled={patchPrincipal.isPending}
          onChange={(e) => handleStatusChange(e.target.value)}
        >
          {PRINCIPAL_STATUSES.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
      </TableCell>
    </TableRow>
  );
}

/** One row in the Flows table: weight is a plain number input + explicit
 * Save button (no implicit on-blur write — an operator editing several
 * flows shouldn't fire a PATCH per accidental tab-away). */
function FlowRow({ principalKey, flow, onSaved }: { principalKey: string; flow: MetaAppFlowRow; onSaved: () => void }) {
  const [weight, setWeight] = useState(String(flow.weight));
  const patchFlow = useAdminApiMutation<unknown, { weight?: number; status?: string }>(
    (vars) => ({
      path: `/admin/meta-apps/flows/${flow.id}`,
      init: { method: 'PATCH', body: JSON.stringify(vars) },
      fallback: `Failed to update ${flow.loginFlow}`,
    }),
    { onSuccess: onSaved },
  );

  return (
    <TableRow>
      <TableCell className="font-medium">{principalKey}</TableCell>
      <TableCell>{flow.loginFlow}</TableCell>
      <TableCell>{flow.configId ?? '—'}</TableCell>
      <TableCell>
        <select
          aria-label={`${flow.loginFlow} status`}
          defaultValue={flow.status}
          disabled={patchFlow.isPending}
          onChange={(e) =>
            patchFlow.mutate(
              { status: e.target.value },
              {
                onSuccess: () => toast.success(`${flow.loginFlow} status updated`),
                onError: (err) => toast.error(err.userMessage ?? `Failed to update ${flow.loginFlow} status`),
              },
            )
          }
        >
          {FLOW_STATUSES.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
      </TableCell>
      <TableCell className="flex items-center gap-2">
        <Input
          type="number"
          min={0}
          aria-label={`${flow.loginFlow} weight`}
          value={weight}
          onChange={(e) => setWeight(e.target.value)}
          className="w-20"
        />
        <Button
          size="sm"
          disabled={patchFlow.isPending}
          onClick={() =>
            patchFlow.mutate(
              { weight: Number(weight) },
              {
                onSuccess: () => toast.success(`${flow.loginFlow} weight updated`),
                onError: (err) => toast.error(err.userMessage ?? `Failed to update ${flow.loginFlow} weight`),
              },
            )
          }
        >
          Save
        </Button>
      </TableCell>
    </TableRow>
  );
}

export function SuperAdminMetaAppsPage() {
  const queryClient = useQueryClient();
  const { data, isLoading } = useAdminApiQuery<RegistryResponse>(
    qk.superAdmin('meta-apps'),
    '/admin/meta-apps',
  );
  const principals = data?.principals ?? [];
  const invalidate = () => void queryClient.invalidateQueries({ queryKey: qk.superAdmin('meta-apps') });

  return (
    <PageContainer variant="fluid" className="space-y-8">
      <section aria-label="Meta app principals" className="space-y-2">
        <h2 className="text-sm font-semibold text-muted-foreground">Apps</h2>
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Key</TableHead>
                <TableHead>Kind</TableHead>
                <TableHead>Connections</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={4} className="h-24 text-center text-sm text-muted-foreground">
                    Loading…
                  </TableCell>
                </TableRow>
              ) : principals.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} className="h-24 text-center text-sm text-muted-foreground">
                    No apps registered.
                  </TableCell>
                </TableRow>
              ) : (
                principals.map((p) => <PrincipalRow key={p.id} principal={p} onSaved={invalidate} />)
              )}
            </TableBody>
          </Table>
        </div>
      </section>

      <section aria-label="Meta app flows" className="space-y-2">
        <h2 className="text-sm font-semibold text-muted-foreground">Signup Flows</h2>
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>App</TableHead>
                <TableHead>Login flow</TableHead>
                <TableHead>Config</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Weight</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={5} className="h-24 text-center text-sm text-muted-foreground">
                    Loading…
                  </TableCell>
                </TableRow>
              ) : principals.flatMap((p) => p.flows).length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="h-24 text-center text-sm text-muted-foreground">
                    No flows configured.
                  </TableCell>
                </TableRow>
              ) : (
                principals.flatMap((p) =>
                  p.flows.map((flow) => (
                    <FlowRow key={flow.id} principalKey={p.key} flow={flow} onSaved={invalidate} />
                  )),
                )
              )}
            </TableBody>
          </Table>
        </div>
      </section>
    </PageContainer>
  );
}
